import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import {
  isProviderOwnerEditable,
  isProviderOwnerUnderReview,
} from "../../provider-owner.state";
import {
  providerOwnerStatusLabel,
  providerOwnerStatusTone,
} from "../../provider-owner.utils";
import type {
  ProviderOwnerOnboardingStatus,
  ProviderOwnerOnboardingStep,
} from "../../provider-owner.types";
import { useProviderOwnerOnboardingStatus } from "../../provider-owner.hooks";

const steps = [
  { key: "start", label: "Start", path: "/provider/onboarding/start" },
  { key: "profile", label: "Business profile", path: "/provider/onboarding/profile" },
  { key: "verification", label: "Verification", path: "/provider/onboarding/verification" },
  { key: "services", label: "Services & pricing", path: "/provider/onboarding/services" },
  { key: "calendar", label: "Calendar & slots", path: "/provider/onboarding/calendar" },
  { key: "deposit-policy", label: "Deposit policy", path: "/provider/onboarding/deposit-policy" },
  { key: "portfolio", label: "Portfolio", path: "/provider/onboarding/portfolio" },
  { key: "review", label: "Review & submit", path: "/provider/onboarding/review" },
  { key: "complete", label: "Complete", path: "/provider/onboarding/complete" },
] as const;

type StepKey = (typeof steps)[number]["key"];

const mapStatusToCurrentStep = (
  status: ProviderOwnerOnboardingStatus,
  fallbackStep: ProviderOwnerOnboardingStep | undefined,
): StepKey | undefined => {
  if (status === "LEAD_CREATED") return "start";

  if (status === "DRAFT_IN_PROGRESS" || status === "AWAITING_PROVIDER_COMPLETION") {
    return (fallbackStep as StepKey | undefined) ?? "start";
  }

  if (status === "READY_FOR_BDO_REVIEW" || status === "READY_FOR_OPS_APPROVAL") {
    return "review";
  }

  if (status === "APPROVED" || status === "ACTIVATION_PENDING" || status === "ACTIVE") {
    return "complete";
  }

  if (status === "RETURNED_BY_BDO" || status === "RETURNED_BY_OPS") {
    return (fallbackStep as StepKey | undefined) ?? "review";
  }

  return (fallbackStep as StepKey | undefined) ?? "start";
};

const OnboardingStepper: React.FC = () => {
  const { user } = useAuth();
  const providerId = user?.providerId ?? "";

  const statusQuery = useProviderOwnerOnboardingStatus(providerId);
  const statusDto = statusQuery.data;

  const currentStatus: ProviderOwnerOnboardingStatus = statusDto?.status ?? "LEAD_CREATED";

  const fallbackStep = user?.providerOnboardingStep as ProviderOwnerOnboardingStep | undefined;

  const currentStep = mapStatusToCurrentStep(currentStatus, fallbackStep);
  const currentIndex = steps.findIndex((step) => step.key === currentStep);

  const isEditable = isProviderOwnerEditable(currentStatus);
  const underReview = isProviderOwnerUnderReview(currentStatus);

  return (
    <aside
      style={{
        background: "#ffffff",
        borderRadius: 20,
        padding: "1rem",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 30px rgba(15, 23, 42, 0.06)",
      }}
    >
      <div style={{ marginBottom: "1rem" }}>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#111827" }}>
          Setup steps
        </p>
        <p
          style={{
            margin: "0.35rem 0 0",
            fontSize: 13,
            lineHeight: 1.55,
            color: "#6b7280",
          }}
        >
          Complete each stage before review and activation.
        </p>

        <div
          style={{
            marginTop: "0.85rem",
            padding: "0.75rem",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            background: "#fafafa",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.75rem",
            }}
          >
            <span style={{ fontSize: 13, color: "#6b7280" }}>Current status</span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color:
                  providerOwnerStatusTone(currentStatus) === "success"
                    ? "#166534"
                    : providerOwnerStatusTone(currentStatus) === "warning"
                      ? "#92400e"
                      : providerOwnerStatusTone(currentStatus) === "danger"
                        ? "#b91c1c"
                        : "#374151",
              }}
            >
              {providerOwnerStatusLabel(currentStatus)}
            </span>
          </div>
          <div style={{ marginTop: "0.4rem", fontSize: 12, color: "#6b7280" }}>
            {isEditable
              ? "You can continue editing this onboarding flow."
              : underReview
                ? "This onboarding is under review and may be read-only."
                : "This onboarding is locked or active."}
          </div>
        </div>
      </div>

      <nav aria-label="Provider onboarding steps">
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gap: "0.5rem",
          }}
        >
          {steps.map((step, index) => {
            const isCompleted = currentIndex >= 0 ? index < currentIndex : false;
            const isCurrent = currentStep === step.key;

            return (
              <li key={step.key}>
                <NavLink
                  to={step.path}
                  style={({ isActive }) => ({
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "center",
                    textDecoration: "none",
                    padding: "0.78rem",
                    borderRadius: 14,
                    color: "#111827",
                    background: isActive ? "#eef4ff" : "#ffffff",
                    border: isActive ? "1px solid #c9dbff" : "1px solid transparent",
                    boxShadow: isActive
                      ? "0 6px 18px rgba(37, 99, 235, 0.08)"
                      : "none",
                    opacity: isEditable || isCurrent || isCompleted ? 1 : 0.8,
                  })}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isCompleted ? "#166534" : isCurrent ? "#2563eb" : "#111827",
                      color: "#ffffff",
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {isCompleted ? "✓" : index + 1}
                  </span>

                  <span style={{ display: "grid", gap: "0.15rem" }}>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {step.label}
                    </span>
                    {isCurrent ? (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#2563eb",
                          fontWeight: 600,
                        }}
                      >
                        Current step
                      </span>
                    ) : isCompleted ? (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#166534",
                          fontWeight: 600,
                        }}
                      >
                        Completed
                      </span>
                    ) : null}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

export default OnboardingStepper;